import type { ProcessImage } from '../memory/ProcessImage';
import type { ParsedAddress } from '../address/parser';
import type { Instruction, ProgramIR, Operand } from './ir';
import type { TimerManager,  } from '../core/TimerManager';
import type { CounterManager } from '../core/CounterManager';

/**
 * A basic interpreter that evaluates IR instructions against a ProcessImage.
 * Uses a simple boolean accumulator (RLO - Result of Logic Operation).
 */
export class Interpreter {
  private rlo = false;
  private stack: boolean[] = [];
  private edgeStates = new Map<string, boolean>();
  private flipFlopS = new Map<string, boolean>();

  constructor(
    private readonly pi: ProcessImage,
    private readonly timers: TimerManager,
    private readonly counters: CounterManager
  ) {}

  reset(): void {
    this.rlo = false;
    this.stack = [];
    this.edgeStates.clear();
    this.flipFlopS.clear();
  }

  execute(program: ProgramIR): void {
    this.rlo = false;
    this.stack = [];

    // Build label map for JMP
    const labelMap = new Map<string, number>();
    for (let i = 0; i < program.length; i++) {
      const inst = program[i];
      if (inst && inst.op === 'LBL' && inst.label) {
        labelMap.set(inst.label, i);
      }
    }

    let ip = 0;
    let steps = 0;
    const maxSteps = 10000;

    while (ip < program.length && steps < maxSteps) {
      steps++;
      const inst = program[ip];
      if (!inst) {
        ip++;
        continue;
      }

      if (inst.op === 'JMP') {
        if (this.rlo && inst.label && labelMap.has(inst.label)) {
          ip = labelMap.get(inst.label)! + 1;
          continue;
        }
        ip++;
        continue;
      }

      this.executeInstruction(inst);
      ip++;
    }
  }

  private executeInstruction(inst: Instruction): void {
    const val = inst.address ? this.readBit(inst.address) : false;

    switch (inst.op) {
      case 'LD':
        this.rlo = val;
        break;
      case 'LDN':
        this.rlo = !val;
        break;
      case 'AND':
        this.rlo = this.rlo && val;
        break;
      case 'ANDN':
        this.rlo = this.rlo && !val;
        break;
      case 'OR':
        this.rlo = this.rlo || val;
        break;
      case 'ORN':
        this.rlo = this.rlo || !val;
        break;
      case 'OUT':
        if (inst.address) this.writeBit(inst.address, this.rlo);
        break;
      case 'OUTN':
        if (inst.address) this.writeBit(inst.address, !this.rlo);
        break;
      case 'SET':
        if (this.rlo && inst.address) this.writeBit(inst.address, true);
        break;
      case 'RESET':
        if (this.rlo && inst.address) this.writeBit(inst.address, false);
        break;
      case 'NOT':
        this.rlo = !this.rlo;
        break;

      case 'P_CONTACT': {
        if (inst.address) {
          const key = inst.sourceElementId ?? `${inst.address.area}${inst.address.byteOffset}.${inst.address.bitOffset}`;
          const prev = this.edgeStates.get(key) ?? false;
          const curr = this.readBit(inst.address);
          const pulse = !prev && curr;
          this.edgeStates.set(key, curr);
          this.rlo = this.rlo && pulse;
        } else {
          this.rlo = false;
        }
        break;
      }

      case 'N_CONTACT': {
        if (inst.address) {
          const key = inst.sourceElementId ?? `${inst.address.area}${inst.address.byteOffset}.${inst.address.bitOffset}`;
          const prev = this.edgeStates.get(key) ?? false;
          const curr = this.readBit(inst.address);
          const pulse = prev && !curr;
          this.edgeStates.set(key, curr);
          this.rlo = this.rlo && pulse;
        } else {
          this.rlo = false;
        }
        break;
      }

      case 'MPS':
        this.stack.push(this.rlo);
        break;
      case 'ALD': {
        const val = this.stack.pop() ?? false;
        this.rlo = this.rlo && val;
        break;
      }
      case 'OLD': {
        const val = this.stack.pop() ?? false;
        this.rlo = this.rlo || val;
        break;
      }

      case 'TON':
      case 'TOF':
      case 'TP':
      case 'TONR':
        if (inst.address && inst.address.area === 'T') {
          const id = `T${inst.address.byteOffset}`;
          this.timers.getOrCreateTimer(id, inst.op, inst.value ?? 0);
          this.timers.updateTimerInput(id, this.rlo);
          const t = this.timers.getTimer(id);
          this.rlo = t ? t.output : false;
        }
        break;

      case 'TONR_RESET':
        if (inst.address && inst.address.area === 'T') {
          const id = `T${inst.address.byteOffset}`;
          if (this.rlo) {
            this.timers.resetTimer(id);
          }
        }
        break;

      case 'CTU':
      case 'CTUD_CU':
        if (inst.address && inst.address.area === 'C') {
          const id = `C${inst.address.byteOffset}`;
          this.counters.getOrCreateCounter(id, inst.op === 'CTU' ? 'CTU' : 'CTUD', inst.value ?? 0);
          this.counters.updateCU(id, this.rlo);
          const c = this.counters.getCounter(id);
          this.rlo = c ? c.q : false;
        }
        break;

      case 'CTD':
      case 'CTUD_CD':
        if (inst.address && inst.address.area === 'C') {
          const id = `C${inst.address.byteOffset}`;
          this.counters.getOrCreateCounter(id, inst.op === 'CTD' ? 'CTD' : 'CTUD', inst.value ?? 0);
          this.counters.updateCD(id, this.rlo);
          const c = this.counters.getCounter(id);
          this.rlo = c ? c.q : false;
        }
        break;

      case 'CTU_RESET':
        if (inst.address && inst.address.area === 'C') {
          const id = `C${inst.address.byteOffset}`;
          if (this.rlo) {
            this.counters.resetCounter(id);
          }
        }
        break;

      case 'CTD_LOAD':
        if (inst.address && inst.address.area === 'C') {
          const id = `C${inst.address.byteOffset}`;
          if (this.rlo) {
            this.counters.loadCounter(id);
          }
        }
        break;

      case 'SR_SET': {
        const key = inst.sourceElementId ?? `${inst.address?.area ?? ''}${inst.address?.byteOffset ?? ''}`;
        this.flipFlopS.set(key, this.rlo);
        if (this.rlo && inst.address) {
          this.writeBit(inst.address, true);
        }
        break;
      }

      case 'SR_RESET': {
        if (inst.address) {
          const key = inst.sourceElementId ?? `${inst.address.area}${inst.address.byteOffset}`;
          const sVal = this.flipFlopS.get(key) ?? false;
          const rVal = this.rlo;
          const currQ = this.readBit(inst.address);
          let newQ = currQ;
          if (sVal) {
            newQ = true; // S dominant
          } else if (rVal) {
            newQ = false;
          }
          this.writeBit(inst.address, newQ);
        }
        break;
      }

      case 'RS_SET': {
        const key = inst.sourceElementId ?? `${inst.address?.area ?? ''}${inst.address?.byteOffset ?? ''}`;
        this.flipFlopS.set(key, this.rlo);
        if (this.rlo && inst.address) {
          this.writeBit(inst.address, true);
        }
        break;
      }

      case 'RS_RESET': {
        if (inst.address) {
          const key = inst.sourceElementId ?? `${inst.address.area}${inst.address.byteOffset}`;
          const sVal = this.flipFlopS.get(key) ?? false;
          const rVal = this.rlo;
          const currQ = this.readBit(inst.address);
          let newQ = currQ;
          if (rVal) {
            newQ = false; // R dominant
          } else if (sVal) {
            newQ = true;
          }
          this.writeBit(inst.address, newQ);
        }
        break;
      }

      case 'CMP_EQ_I':
      case 'CMP_NE_I':
      case 'CMP_GT_I':
      case 'CMP_LT_I':
      case 'CMP_GE_I':
      case 'CMP_LE_I': {
        if (inst.operands && inst.operands.length === 2 && this.rlo) {
          const op1 = inst.operands[0];
          const op2 = inst.operands[1];
          if (!op1 || !op2) break;
          const v1 = this.getOperandValue(op1);
          const v2 = this.getOperandValue(op2);
          let cmpRes = false;
          switch (inst.op) {
            case 'CMP_EQ_I': cmpRes = v1 === v2; break;
            case 'CMP_NE_I': cmpRes = v1 !== v2; break;
            case 'CMP_GT_I': cmpRes = v1 > v2; break;
            case 'CMP_LT_I': cmpRes = v1 < v2; break;
            case 'CMP_GE_I': cmpRes = v1 >= v2; break;
            case 'CMP_LE_I': cmpRes = v1 <= v2; break;
          }
          this.rlo = cmpRes;
        } else {
          this.rlo = false;
        }
        break;
      }

      case 'CMP_EQ_R':
      case 'CMP_NE_R':
      case 'CMP_GT_R':
      case 'CMP_LT_R':
      case 'CMP_GE_R':
      case 'CMP_LE_R': {
        if (inst.operands && inst.operands.length === 2 && this.rlo) {
          const op1 = inst.operands[0];
          const op2 = inst.operands[1];
          if (!op1 || !op2) break;
          const v1 = this.getOperandFloat(op1);
          const v2 = this.getOperandFloat(op2);
          let cmpRes = false;
          switch (inst.op) {
            case 'CMP_EQ_R': cmpRes = Math.abs(v1 - v2) < 0.00001; break;
            case 'CMP_NE_R': cmpRes = Math.abs(v1 - v2) >= 0.00001; break;
            case 'CMP_GT_R': cmpRes = v1 > v2; break;
            case 'CMP_LT_R': cmpRes = v1 < v2; break;
            case 'CMP_GE_R': cmpRes = v1 >= v2; break;
            case 'CMP_LE_R': cmpRes = v1 <= v2; break;
          }
          this.rlo = cmpRes;
        } else {
          this.rlo = false;
        }
        break;
      }

      case 'ADD_I':
      case 'SUB_I':
      case 'MUL_I':
      case 'DIV_I': {
        if (inst.operands && inst.operands.length === 2 && inst.address && this.rlo) {
          const op1 = inst.operands[0];
          const op2 = inst.operands[1];
          if (!op1 || !op2) break;
          const v1 = this.getOperandValue(op1);
          const v2 = this.getOperandValue(op2);
          let res = 0;
          switch (inst.op) {
            case 'ADD_I': res = v1 + v2; break;
            case 'SUB_I': res = v1 - v2; break;
            case 'MUL_I': res = Math.imul(v1, v2); break;
            case 'DIV_I': 
              res = v2 !== 0 ? Math.trunc(v1 / v2) : 0;
              break;
          }
          // Int16 overflow handling
          res = (res & 0xFFFF);
          if (res > 32767) res -= 65536;
          this.writeInt(inst.address, res);
        }
        break;
      }

      case 'ADD_R':
      case 'SUB_R':
      case 'MUL_R':
      case 'DIV_R': {
        if (inst.operands && inst.operands.length === 2 && inst.address && this.rlo) {
          const op1 = inst.operands[0];
          const op2 = inst.operands[1];
          if (!op1 || !op2) break;
          const v1 = this.getOperandFloat(op1);
          const v2 = this.getOperandFloat(op2);
          let res = 0;
          switch (inst.op) {
            case 'ADD_R': res = v1 + v2; break;
            case 'SUB_R': res = v1 - v2; break;
            case 'MUL_R': res = v1 * v2; break;
            case 'DIV_R': res = v2 !== 0 ? v1 / v2 : 0; break;
          }
          this.writeFloat(inst.address, res);
        }
        break;
      }

      case 'MOV_W': {
        if (inst.operands && inst.operands.length === 1 && inst.address && this.rlo) {
          const op1 = inst.operands[0];
          if (!op1) break;
          const v = this.getOperandValue(op1);
          this.writeInt(inst.address, v);
        }
        break;
      }

      case 'MOV_R': {
        if (inst.operands && inst.operands.length === 1 && inst.address && this.rlo) {
          const op1 = inst.operands[0];
          if (!op1) break;
          const v = this.getOperandFloat(op1);
          this.writeFloat(inst.address, v);
        }
        break;
      }

      case 'SCALE': {
        if (inst.scaleParams && inst.operands && inst.operands.length === 1 && inst.address && this.rlo) {
          const inVal = this.getOperandFloat(inst.operands[0]!);
          const inMin = this.getOperandFloat(inst.scaleParams.inMin);
          const inMax = this.getOperandFloat(inst.scaleParams.inMax);
          const outMin = this.getOperandFloat(inst.scaleParams.outMin);
          const outMax = this.getOperandFloat(inst.scaleParams.outMax);

          let scaled = outMin;
          if (inMax !== inMin) {
            const fraction = (inVal - inMin) / (inMax - inMin);
            scaled = outMin + fraction * (outMax - outMin);
          }

          if (inst.address.size === 'D') {
            this.writeFloat(inst.address, scaled);
          } else {
            this.writeInt(inst.address, Math.round(scaled));
          }
        }
        break;
      }

      case 'IN_RANGE': {
        if (inst.operands && inst.operands.length === 3 && this.rlo) {
          const val = this.getOperandFloat(inst.operands[0]!);
          const min = this.getOperandFloat(inst.operands[1]!);
          const max = this.getOperandFloat(inst.operands[2]!);
          this.rlo = (val >= min && val <= max);
        } else {
          this.rlo = false;
        }
        break;
      }

      case 'LBL':
        // Label position marker - no operation during execution
        break;
    }
  }

  private readBit(addr: ParsedAddress): boolean {
    if (addr.size !== 'X' && addr.area !== 'T' && addr.area !== 'C') return false;

    switch (addr.area) {
      case 'I':
        return this.pi.inputs.getBit(addr.byteOffset, addr.bitOffset);
      case 'Q':
        return this.pi.outputs.getBit(addr.byteOffset, addr.bitOffset);
      case 'M':
        return this.pi.memory.getBit(addr.byteOffset, addr.bitOffset);
      case 'T': {
        const id = `T${addr.byteOffset}`;
        const t = this.timers.getTimer(id);
        return t ? t.output : false;
      }
      case 'C': {
        const id = `C${addr.byteOffset}`;
        const c = this.counters.getCounter(id);
        return c ? c.q : false;
      }
      default:
        return false;
    }
  }

  private writeBit(addr: ParsedAddress, value: boolean): void {
    if (addr.size !== 'X') return;

    switch (addr.area) {
      case 'I':
        this.pi.inputs.setBit(addr.byteOffset, addr.bitOffset, value);
        break;
      case 'Q':
        this.pi.outputs.setBit(addr.byteOffset, addr.bitOffset, value);
        break;
      case 'M':
        this.pi.memory.setBit(addr.byteOffset, addr.bitOffset, value);
        break;
    }
  }

  private getOperandValue(op: Operand): number {
    if (op.type === 'CONSTANT') return op.value;
    const addr = op.addr;
    if (addr.size === 'X') return this.readBit(addr) ? 1 : 0;
    
    switch (addr.area) {
      case 'M': return this.pi.memory.getInt(addr.byteOffset);
      case 'I': return this.pi.inputs.getInt(addr.byteOffset);
      case 'Q': return this.pi.outputs.getInt(addr.byteOffset);
      case 'T': return this.timers.getTimer(`T${addr.byteOffset}`)?.elapsedMs ?? 0;
      case 'C': return this.counters.getCounter(`C${addr.byteOffset}`)?.cv ?? 0;
    }
    return 0;
  }

  private getOperandFloat(op: Operand): number {
    if (op.type === 'CONSTANT') return op.value;
    const addr = op.addr;
    if (addr.size === 'D') {
      switch (addr.area) {
        case 'M': return this.pi.memory.getFloat(addr.byteOffset);
        case 'I': return this.pi.inputs.getFloat(addr.byteOffset);
        case 'Q': return this.pi.outputs.getFloat(addr.byteOffset);
      }
    }
    return this.getOperandValue(op);
  }

  private writeInt(addr: ParsedAddress, value: number): void {
    if (addr.area === 'M') {
      this.pi.memory.setInt(addr.byteOffset, value);
    } else if (addr.area === 'Q') {
      this.pi.outputs.setInt(addr.byteOffset, value);
    }
  }

  private writeFloat(addr: ParsedAddress, value: number): void {
    if (addr.area === 'M') {
      this.pi.memory.setFloat(addr.byteOffset, value);
    } else if (addr.area === 'Q') {
      this.pi.outputs.setFloat(addr.byteOffset, value);
    }
  }
}
